// Market Watch Chart Generator
// Generates charts from flattened pivot data (grouped_grid with _level hierarchy)

import { DynamicChart, EnrichedData } from "./types";
import { DATA_DETECTION, CHART_LIMITS } from "../../../shared/constants";

// Helper to create clean display name from column header
const cleanColumnName = (name: string): string => {
  return name
    .replace(/^(sum|avg|count|total|mean|max|min)_/gi, "")
    .replace(/_(sum|avg|count|total|mean|max|min)$/gi, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, l => l.toUpperCase())
    .trim();
};

// Helper to truncate label
const truncateLabel = (label: string, maxLen: number = 18): string => {
  if (label.length <= maxLen) return label;
  return label.substring(0, maxLen - 3) + "...";
};

// Main: Generate charts based on flattened pivot data (grouped_grid)
export const generateDynamicCharts = (
  enrichedData: EnrichedData,
  _htmlReport: string,
  _summary: string
): DynamicChart[] => {
  const headers = enrichedData.headers;
  const headersLower = headers.map(h => (h || "").toLowerCase());
  const rows = enrichedData.rows;

  if (rows.length === 0) {
    console.log("No rows in flattened pivot data, skipping charts");
    return [];
  }

  const charts: DynamicChart[] = [];

  // Check if we have _level column (hierarchical grouped_grid)
  const levelIdx = headersLower.indexOf("_level");

  // Get Level 1 rows (first-level subtotals) for main charts
  // These are aggregates by the primary dimension
  let level1Rows: any[][] = [];
  let detailRows: any[][] = [];

  if (levelIdx >= 0) {
    level1Rows = rows.filter(r => {
      const level = parseInt(r[levelIdx]);
      return level === 1;
    });
    // Get highest level rows (most detailed)
    const maxLevel = Math.max(...rows.map(r => parseInt(r[levelIdx]) || 0));
    detailRows = rows.filter(r => parseInt(r[levelIdx]) === maxLevel);
  } else {
    // No hierarchy - use all rows as detail rows
    detailRows = rows;
  }

  // Use level 1 data if available, otherwise use all detail rows
  const chartSourceRows = level1Rows.length >= 2 ? level1Rows : detailRows;

  console.log("Chart Analysis:", {
    hasLevelColumn: levelIdx >= 0,
    level1Rows: level1Rows.length,
    detailRows: detailRows.length,
    chartSourceRows: chartSourceRows.length,
    headers: headers.filter(h => !h.startsWith("_"))
  });

  // Find categorical columns (dimensions - non-numeric, non-internal)
  const categoricalColumns: Array<{ idx: number; name: string }> = [];
  for (let i = 0; i < headers.length; i++) {
    if (headers[i].startsWith("_")) continue;

    const headerLower = headersLower[i];

    // Check if this column has non-empty string values
    const values = chartSourceRows.map(r => r[i]).filter(v => v != null && v !== "" && v !== "Grand Total");
    if (values.length === 0) continue;

    // Known dimension column patterns (common in Market Watch)
    const isDimensionKeyword = headerLower.includes("asin") ||
                               headerLower.includes("keyword") ||
                               headerLower.includes("brand") ||
                               headerLower.includes("category") ||
                               headerLower.includes("cluster") ||
                               headerLower.includes("label") ||
                               headerLower.includes("segment") ||
                               headerLower.includes("product") ||
                               headerLower.includes("name") ||
                               headerLower.includes("type") ||
                               headerLower.includes("group");

    // Check if mostly non-numeric OR is a known dimension column
    const numericCount = values.filter(v => !isNaN(parseFloat(String(v)))).length;
    const isNonNumeric = numericCount < values.length * DATA_DETECTION.MIN_NUMERIC_RATIO;

    if (isNonNumeric || isDimensionKeyword) {
      // Double-check: if it's a dimension keyword but values look numeric, skip
      if (isDimensionKeyword && !isNonNumeric && numericCount > values.length * DATA_DETECTION.HIGH_NUMERIC_RATIO) {
        continue; // Actually numeric despite keyword
      }
      categoricalColumns.push({ idx: i, name: headers[i] });
    }
  }

  // Find numeric columns with their types
  const numericColumns: Array<{
    idx: number;
    name: string;
    type: "sum" | "avg" | "count" | "percent" | "other";
    values: number[];
  }> = [];

  for (let i = 0; i < headers.length; i++) {
    if (headers[i].startsWith("_")) continue;

    const headerLower = headersLower[i];
    const values = chartSourceRows
      .map(r => parseFloat(r[i]))
      .filter(v => !isNaN(v) && isFinite(v));

    if (values.length < chartSourceRows.length * DATA_DETECTION.MIN_NUMERIC_RATIO) continue;
    if (values.every(v => v === 0)) continue;

    // Determine column type based on keywords in column name
    // Priority: explicit suffixes > keywords > default based on value magnitude
    let type: "sum" | "avg" | "count" | "percent" | "other" = "other";

    // Check for explicit aggregation suffixes first
    if (headerLower.includes("_sum") || headerLower.includes("sum_")) {
      type = "sum";
    } else if (headerLower.includes("_avg") || headerLower.includes("avg_") || headerLower.includes("average")) {
      type = "avg";
    } else if (headerLower.includes("_count") || headerLower.includes("count_") || headerLower.endsWith("_count")) {
      type = "count";
    } else if (headerLower.includes("share") || headerLower.includes("percent") || headerLower.includes("%")) {
      type = "percent";
    }
    // Check for keyword-based type detection (for columns without suffixes)
    else if (headerLower.includes("volume") || headerLower.includes("forecast") ||
             headerLower.includes("demand") || headerLower.includes("purchase") ||
             headerLower.includes("sales") || headerLower.includes("revenue") ||
             headerLower.includes("units") || headerLower.includes("quantity") ||
             headerLower.includes("total") || headerLower.includes("amount")) {
      type = "sum"; // Volume/quantity metrics are typically summed
    } else if (headerLower.includes("price") || headerLower.includes("cost") ||
               headerLower.includes("rating") || headerLower.includes("score") ||
               headerLower.includes("rate") && !headerLower.includes("share") || headerLower.includes("margin")) {
      type = "avg"; // Price/rating metrics are typically averaged
    } else if (headerLower.includes("count") || headerLower.includes("num") ||
               headerLower.includes("number")) {
      type = "count";
    } else {
      // Default: treat as sum for large values, avg for small values
      const maxVal = Math.max(...values);
      type = maxVal > DATA_DETECTION.METRIC_SUM_THRESHOLD ? "sum" : "avg";
    }

    numericColumns.push({ idx: i, name: headers[i], type, values });
  }

  console.log("=== CHART COLUMN DETECTION ===");
  console.log("Categorical columns found:", categoricalColumns.length, categoricalColumns.map(c => c.name));
  console.log("Numeric columns found:", numericColumns.length);
  console.log("Numeric column details:", numericColumns.map(c => ({
    name: c.name,
    type: c.type,
    sampleValues: c.values.slice(0, 3)
  })));

  if (categoricalColumns.length === 0 || numericColumns.length === 0) {
    console.log("Not enough columns for charts");
    return [];
  }

  // Smart category selection: pick the categorical column with the most unique values
  // This ensures meaningful charts (e.g., if single ASIN selected, use price/feature cluster instead)
  const categoryWithCounts = categoricalColumns.map(col => {
    const uniqueValues = new Set(
      chartSourceRows
        .map(r => r[col.idx])
        .filter(v => v != null && v !== "" && v !== "Grand Total")
    );
    return { ...col, uniqueCount: uniqueValues.size };
  });

  // Sort by unique count descending - prefer categories with more variety for meaningful charts
  categoryWithCounts.sort((a, b) => b.uniqueCount - a.uniqueCount);

  console.log("Category selection:", categoryWithCounts.map(c => ({ name: c.name, uniqueCount: c.uniqueCount })));

  // Primary category should have at least 2 unique values for a meaningful chart
  const primaryCategory = categoryWithCounts.find(c => c.uniqueCount >= 2) || categoryWithCounts[0];

  // Secondary category for variety (different from primary, with good unique count)
  const secondaryCategory = categoryWithCounts.find(c => c.idx !== primaryCategory.idx && c.uniqueCount >= 2);

  console.log("Selected primary category:", primaryCategory.name, "with", primaryCategory.uniqueCount, "unique values");
  if (secondaryCategory) {
    console.log("Selected secondary category:", secondaryCategory.name, "with", secondaryCategory.uniqueCount, "unique values");
  }

  const usedMetrics = new Set<number>();

  // CHART 1: Primary volume/demand metric by main category
  const volumeMetric = numericColumns.find(c =>
    c.type === "sum" &&
    (c.name.toLowerCase().includes("volume") ||
     c.name.toLowerCase().includes("purchase") ||
     c.name.toLowerCase().includes("forecast") ||
     c.name.toLowerCase().includes("demand"))
  ) || numericColumns.find(c => c.type === "sum");

  if (volumeMetric) {
    usedMetrics.add(volumeMetric.idx);

    const chartData = chartSourceRows
      .map(r => ({
        label: truncateLabel(String(r[primaryCategory.idx] || "Other")),
        value: parseFloat(r[volumeMetric.idx]) || 0
      }))
      .filter(d => d.value > 0 && d.label !== "Grand Total")
      .sort((a, b) => b.value - a.value)
      .slice(0, CHART_LIMITS.MAX_DATA_POINTS);

    if (chartData.length >= 2) {
      charts.push({
        id: "chart_volume",
        title: `${cleanColumnName(volumeMetric.name)} by ${cleanColumnName(primaryCategory.name)}`,
        type: chartData.length <= CHART_LIMITS.PIE_CHART_MAX_ITEMS ? "pie" : "bar",
        data: chartData.map(d => ({ ...d, value: Math.round(d.value) })),
        priority: 10,
      });
    }
  }

  // CHART 2: Show the SAME primary metric by DIFFERENT category (secondary category)
  // This gives a different perspective on the same data
  if (secondaryCategory && secondaryCategory.uniqueCount >= 2 && volumeMetric) {
    // Aggregate the same volume metric by secondary category
    const categoryAggregates: Record<string, number> = {};

    chartSourceRows.forEach(r => {
      const category = String(r[secondaryCategory.idx] || "Other");
      if (category && category !== "Grand Total" && category !== "") {
        const value = parseFloat(r[volumeMetric.idx]) || 0;
        categoryAggregates[category] = (categoryAggregates[category] || 0) + value;
      }
    });

    const chartData = Object.entries(categoryAggregates)
      .map(([label, value]) => ({ label: truncateLabel(label), value }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, CHART_LIMITS.MAX_DATA_POINTS);

    if (chartData.length >= 2) {
      charts.push({
        id: "chart_secondary",
        title: `${cleanColumnName(volumeMetric.name)} by ${cleanColumnName(secondaryCategory.name)}`,
        type: chartData.length <= CHART_LIMITS.PIE_CHART_MAX_ITEMS ? "pie" : "column",
        data: chartData.map(d => ({ ...d, value: Math.round(d.value) })),
        priority: 9,
      });
    }
  }

  // CHART 3: If we have another sum metric, show it by primary or secondary category
  const secondSumMetric = numericColumns.find(c =>
    c.type === "sum" && c.idx !== (volumeMetric?.idx ?? -1)
  );

  if (charts.length < 3 && secondSumMetric) {
    // Use primary category for this metric
    const chart3Category = primaryCategory;

    const categoryAggregates: Record<string, number> = {};
    chartSourceRows.forEach(r => {
      const category = String(r[chart3Category.idx] || "Other");
      if (category && category !== "Grand Total" && category !== "") {
        const value = parseFloat(r[secondSumMetric.idx]) || 0;
        categoryAggregates[category] = (categoryAggregates[category] || 0) + value;
      }
    });

    const chartData = Object.entries(categoryAggregates)
      .map(([label, value]) => ({ label: truncateLabel(label), value }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, CHART_LIMITS.MAX_DATA_POINTS);

    if (chartData.length >= 2) {
      usedMetrics.add(secondSumMetric.idx);
      charts.push({
        id: "chart_tertiary",
        title: `${cleanColumnName(secondSumMetric.name)} by ${cleanColumnName(chart3Category.name)}`,
        type: chartData.length <= CHART_LIMITS.PIE_CHART_MAX_ITEMS ? "doughnut" : "bar",
        data: chartData.map(d => ({ ...d, value: Math.round(d.value) })),
        priority: 8,
      });
    }
  }


  // If we still don't have enough charts, try COUNT metric
  if (charts.length < 2) {
    const countMetric = numericColumns.find(c =>
      c.type === "count" && !usedMetrics.has(c.idx)
    );

    if (countMetric) {
      usedMetrics.add(countMetric.idx);

      const chartData = chartSourceRows
        .map(r => ({
          label: truncateLabel(String(r[primaryCategory.idx] || "Other")),
          value: parseFloat(r[countMetric.idx]) || 0
        }))
        .filter(d => d.value > 0 && d.label !== "Grand Total")
        .sort((a, b) => b.value - a.value)
        .slice(0, CHART_LIMITS.MAX_DATA_POINTS);

      if (chartData.length >= 2) {
        charts.push({
          id: "chart_count",
          title: `${cleanColumnName(countMetric.name)} by ${cleanColumnName(primaryCategory.name)}`,
          type: chartData.length <= CHART_LIMITS.PIE_CHART_MAX_ITEMS ? "pie" : "bar",
          data: chartData.map(d => ({ ...d, value: Math.round(d.value) })),
          priority: 7,
        });
      }
    }
  }

  // Sort by priority and return top 3 charts
  const result = charts
    .sort((a, b) => b.priority - a.priority)
    .slice(0, CHART_LIMITS.MAX_CHARTS);

  console.log("Generated Charts:", result.map(c => ({ title: c.title, type: c.type, dataPoints: c.data.length })));

  return result;
};
